import { DateTime } from "luxon";
import {
  authAxios,
  billingAxios,
  registrationAxios,
  reservationAxios,
  riderAxios,
  vehicleAxios,
  geocodingAxios,
  geofenceAxios,
} from "./axios";
import {
  accessTokenCookieKey,
  scopesCookieKey,
  refreshTokenCookieKey,
  setCookies,
} from "./cookies";
import { StringUtils } from "../util/strings";

export class ApiViewSet {
  constructor(axios, path) {
    this.axios = axios;
    this.path = path;
  }

  async get(id) {
    const res = await this.axios.get(`/${this.path}/${id}`);
    return res.data;
  }

  async list(params) {
    const res = await this.axios.get(`/${this.path}`, { params });
    return res.data.results;
  }

  async create(data) {
    const res = await this.axios.post(`/${this.path}`, data);
    return res.data;
  }

  async patch(id, data) {
    const res = await this.axios.patch(`/${this.path}/${id}`, data);
    return res.data;
  }
}

const auth = {
  login: async (username, password) => {
    const res = await authAxios.post("/o/token/", {
      username,
      password,
      grant_type: "password",
      client_id: window.REACT_APP_AUTH_CLIENT_ID,
    });
    const {
      access_token: accessToken,
      scope,
      refresh_token: refreshToken,
      expires_in: expiresIn,
    } = res.data;
    setCookies(scopesCookieKey, scope, expiresIn);
    setCookies(accessTokenCookieKey, accessToken, expiresIn);
    setCookies(refreshTokenCookieKey, refreshToken);
    return res.data;
  },
  requestNewPassword: async (email) => {
    const res = await authAxios.post("/auth_server/request_change_password/", {
      email,
    });
    return res.data;
  },
  changePassword: async (password, token) => {
    const res = await authAxios.post("/auth_server/change_password", {
      password,
      token,
    });
    return res.data;
  },
};

const fleets = {
  list: async (params) => {
    const res = await vehicleAxios.get("/fleets", { params: { ...params, page_size: 500 } });
    return res.data.results;
  },
  get: async (id, params) => {
    const res = await vehicleAxios.get(`/fleets/${id}`, { params });
    return res.data;
  },
  listVehicles: async (id, params) => {
    const res = await vehicleAxios.get(`/fleets/${id}/vehicles`, { params });
    return res.data;
  },
  create: async (data) => {
    const res = await vehicleAxios.post("/fleets", data);
    return res.data;
  },
  patch: async (id, data) => {
    const res = await vehicleAxios.patch(`/fleets/${id}`, data);
    return res.data;
  },
  getFirmwareEvents: async (params) => {
    const res = await vehicleAxios.get("/automated-vehicle-events/get_all_by_component_version", {
      params,
    });
    return res.data;
  },
  hibernateVehicles: async (id, vehicles) => {
    const data = {
      vehicles,
    };
    const res = await vehicleAxios.post(`/fleets/${id}/hibernate_vehicles`, data);
    return res.data;
  },
  hibernateVehiclesDetails: async (id, vehicles) => {
    const data = {
      vehicles,
    };
    const res = await vehicleAxios.post(`/fleets/${id}/get_hibernate_vehicles_details`, data);
    return res.data;
  },
};

const markets = {
  list: async (params) => {
    const res = await vehicleAxios.get("/markets", { params });
    return res.data.results;
  },
  create: async (data) => {
    const res = await vehicleAxios.post("/markets", data);
    return res.data;
  },
  patch: async (id, data) => {
    const res = await vehicleAxios.patch(`/markets/${id}`, data);
    return res.data;
  },
};

const vehicle = {
  // Is not cache-backed, slower than fleets/id/vehicles but offers option to fetch components
  list: async (params) => {
    const res = await vehicleAxios.get("/vehicles/", { params });
    return res.data;
  },
  listOtaVehicles: async (params) => {
    const res = await vehicleAxios.get("/vehicles-for-ota", { params });
    return res.data;
  },
  get: async (id, params) => {
    const res = await vehicleAxios.get(`/vehicles/${id}`, { params });
    return res.data;
  },
  patch: async (id, data) => {
    const res = await vehicleAxios.patch(`/vehicles/${id}`, data, { params: { fulldata: true } });
    return res.data;
  },
  startTrip: async (id, unlock = false) => {
    const res = await vehicleAxios.post(`/vehicles/${id}/request_start_trip/`, null, {
      params: { fulldata: true, unlock },
    });
    return res.data;
  },
  endTrip: async (id) => {
    const res = await vehicleAxios.post(`/vehicles/${id}/request_stop_trip/`, null, {
      params: { fulldata: true },
    });
    return res.data;
  },
  setState: async (id, state) => {
    const res = await vehicleAxios.post(
      `/vehicles/${id}/request_set_state/`,
      { state },
      { params: { fulldata: true } },
    );
    await vehicleAxios.post(`/vehicles/${id}/request_fetch_mailbox/`);
    return res.data;
  },
  setSpeedLimit: async (id, speed) => {
    await vehicleAxios.post(`/vehicles/${id}/request_configure_vehicle/`, { speed });
    const res = await vehicleAxios.patch(
      `/vehicles/${id}/`,
      { max_speed_limit: speed },
      { params: { fulldata: true } },
    );
    return res.data;
  },
  keyExchange: async (id) => {
    const res = await vehicleAxios.post(`/vehicles/${id}/request_key_exchange/`, null, {
      params: { fulldata: true },
    });
    return res.data;
  },
  refreshRadioCert: async (id) => {
    const res = await vehicleAxios.post(`/vehicles/${id}/refresh_radio_cert/`, null, {
      params: { fulldata: true },
    });
    return res.data;
  },
  reset: async (id) => {
    const res = await vehicleAxios.post(`/vehicles/${id}/request_remote_reset/`, null, {
      params: { fulldata: true },
    });
    await vehicleAxios.post(`/vehicles/${id}/request_fetch_mailbox/`);
    return res.data;
  },
  shutdown: async (id) => {
    const res = await vehicleAxios.post(`/vehicles/${id}/request_remote_shutdown/`, null, {
      params: { fulldata: true },
    });
    await vehicleAxios.post(`/vehicles/${id}/request_fetch_mailbox/`);
    return res.data;
  },
  ping: async (id) => {
    const res = await vehicleAxios.post(`/vehicles/${id}/request_ping/`, null, {
      params: { fulldata: true },
    });
    return res.data;
  },
  unlockBattery: async (id) => {
    const res = await vehicleAxios.post(`/vehicles/${id}/request_unlock_battery/`, null, {
      params: { fulldata: true },
    });
    return res.data;
  },
  unlock: async (id) => {
    const res = await vehicleAxios.post("bulk-field-actions", {
      vehicle_ids: [id],
      action: "unlock",
    });
    return res.data;
  },
  bulkUpdate: async (vehicles) => {
    const res = await vehicleAxios.patch("/vehicles/bulk_update", vehicles);
    return res.data;
  },
  diagnostics: async (vehicleId) => {
    const res = await vehicleAxios.get(`/vehicles/${vehicleId}/diagnostics`);
    const timestamp = DateTime.now().toISO();
    return { diagnosticsData: res.data, timestamp };
  },
  timeline: async (vehicleId, params = {}, signal = undefined) => {
    const res = await vehicleAxios.get(`/vehicles/${vehicleId}/timeline`, { params, signal });
    return res.data;
  },
  simConfig: async (vehicleId) => {
    const cacheKey = `VEHICLE_SIM_${vehicleId}`;
    const res = await vehicleAxios.get(`/vehicles/${vehicleId}/sim`, {
      cacheKey,
      cacheTime: 60 * 5,
    });
    return res.data;
  },
};

const firmware = {
  get: async (id) => {
    const res = await vehicleAxios.get(`/firmware/${id}`);
    return res.data;
  },
  list: async (params) => {
    const res = await vehicleAxios.get("/firmware", { params });
    return res.data.results;
  },
};

const firmwarePackages = {
  list: async (params) => {
    const res = await vehicleAxios.get("/firmware-package", { params });
    return res.data.results;
  },
};

const firmwarePackageApplications = {
  listInProgress: async (params) => {
    const res = await vehicleAxios.get("/firmware-package-application", {
      params: { in_progress: true, ...params },
    });
    return res.data.results;
  },
  bulkStart: async (data) => {
    const res = await vehicleAxios.post("/firmware-package-application/bulk_start", data);
    return res.data;
  },
  bulkStop: async (data) => {
    const res = await vehicleAxios.post("/firmware-package-application/bulk_stop", data);
    return res.data;
  },
};

const geofencePackages = {
  list: async (params) => {
    const res = await vehicleAxios.get("/geofence-packages", { params });
    // The /geofence-packages endpoint won't return a full resource url, but other
    // parts of the app need a resource url. So add it here.
    return res.data.results.map((gfp) => ({
      ...gfp,
      url: StringUtils.uuidToUrl("/vehicle-api/geofence-packages", gfp.id),
    }));
  },
  getById: async (id) => {
    const resp = await vehicleAxios.get(`/geofence-packages/${id}`);
    return resp.data;
  },
};

const geofencePackageApplications = {
  listInProgress: async (params) => {
    const res = await vehicleAxios.get("/geofence-package-application", {
      params: { in_progress: true, ...params },
    });
    return res.data.results;
  },
  bulkStart: async (data) => {
    // The geofence-package-application endpoint takes the uuid of
    // the geofence package, not a resource url like other POST/PATCH operations
    // require, so check if we're passing a url value here and convert it
    // to just the uuid.
    if (/http/.test(data.geofence_package)) {
      data.geofence_package = StringUtils.uuidFromUrl(data.geofence_package);
    }
    const res = await vehicleAxios.post("/geofence-package-application/bulk_start", data);
    return res.data;
  },
  bulkStop: async (data) => {
    const res = await vehicleAxios.post("/geofence-package-application/bulk_stop", data);
    return res.data;
  },
};

const geofences = {
  list: async (params) => {
    const res = await vehicleAxios.get("/geofences", {
      params: { ...params, include_incentive: true },
    });
    return res.data;
  },
  listAll: async (params, _page = 1, _fencesAcc = []) => {
    const { results, next } = await geofences.list({
      ...params,
      page: _page,
    });

    if (next) {
      return geofences.listAll(params, _page + 1, _fencesAcc.concat(results));
    }

    return _fencesAcc.concat(results);
  },
  createGeofence: async (data = {}) => {
    const res = await vehicleAxios.post("/geofences", data);
    return res.data;
  },
  createBulkGeofences: async (data = {}) => {
    const res = await vehicleAxios.post("/geofences/bulk_create", data);
    return res.data;
  },
  createScheduledGeofence: async (data = {}) => {
    const res = await reservationAxios.post("/scheduled-geofences", data);
    return res.data;
  },
  deleteSchedule: async ({ geofenceId, scheduleId } = {}) => {
    await reservationAxios({
      method: "delete",
      url: "/scheduled-geofences",
      data: {
        geofence_id: geofenceId,
        schedule_id: scheduleId,
      },
    });
  },
  updateGeofence: async (id, data = {}, hideSidewalks = true) => {
    const res = await vehicleAxios.patch(`/geofences/${id}?hide_sidewalks=${hideSidewalks}`, data);
    return res.data;
  },
  deleteGeofence: async (id, hideSidewalks = true) => {
    await vehicleAxios.delete(`/geofences/${id}?hide_sidewalks=${hideSidewalks}`);
  },
  createRule: async (data = {}, params) => {
    const res = await vehicleAxios.post("/rules", data, {
      params: { ...params, include_incentive: true },
    });
    return res.data;
  },
  updateRule: async (id, data = {}) => {
    const res = await vehicleAxios.patch(`/rules/${id}`, data);
    return res.data;
  },
  deleteRule: async (id) => {
    await vehicleAxios.delete(`/rules/${id}`);
  },
  createRuleMap: async ({ geofenceId, ruleId } = {}) => {
    const data = { rules: [{ geofences: [geofenceId], rule: ruleId }] };
    const res = await vehicleAxios.post("/geofence-rule-maps", data);
    return res.data;
  },
};

const trips = {
  get: async (id) => {
    const res = await vehicleAxios.get(`/trips/${id}`);
    return res.data;
  },
  getLogs: async (id) => {
    const res = await vehicleAxios.get(`/trips/${id}/view_trip_logs/`);
    return res.data;
  },
  getByVehicle: async (id, earliestTimestamp = null) => {
    const res = await vehicleAxios.get("/trips", {
      params: { ...(earliestTimestamp ? { start__gte: earliestTimestamp } : {}), vehicle: id },
    });
    return res.data.results;
  },
};

const riders = {
  list: async (params) => {
    const res = await riderAxios.get("/all-riders", { params });
    return res.data;
  },
  getRider: async (id) => {
    const res = await riderAxios.get("/all-riders", { params: { id, allow_deleted: true } });
    return res.data;
  },
  banOrSuspend: async (id, start, end, reason, notes) => {
    const data = {
      user_id: id,
      start,
      end,
      reason,
      notes,
    };
    const res = await riderAxios.post("/suspensions", data);
    return res.data;
  },
  activate: async (suspensionId) => {
    const data = {
      id: suspensionId,
      end: DateTime.now().toISO(),
    };
    const res = await riderAxios.patch(`/suspensions/${suspensionId}`, data);
    return res.data;
  },
  softDelete: async (id) => {
    // Soft Delete Account means:
    // 1. set is_deleted field to true
    // 2. remove all PII
    // 3. preserve riderId
    const data = {
      is_deleted: true,
      name: "",
      email: "",
      last_name: "",
      first_name: "",
      gender: null,
      username: null,
      phone_number: "",
      date_of_birth: null,
      unverified_email: null,
    };
    await riderAxios.patch(`/all-riders/${id}`, data);
  },
  getRiderDevices: async (riderId) => {
    const res = await riderAxios.get("/all-riders/devices", { params: { user_id: riderId } });
    return res.data || [];
  },

  updateRiderInformation: async (id, data) => {
    const res = await riderAxios.patch(`/all-riders/${id}`, data);
    return res.data;
  },
};

const reservations = {
  get: async (id) => {
    const res = await reservationAxios.get(`/reservations-with-trip/${id}`);
    return res.data;
  },
  getByRider: async (id, params = {}) => {
    const res = await reservationAxios.get("/reservations-with-trip", {
      params: { ...params, rider: id },
    });
    return res.data;
  },
  getByTripId: async (tripId) => {
    const res = await reservationAxios.get(`/reservations/find-by-trip?trip_id=${tripId}`);
    return res.data;
  },
  endReservation: async (id) => {
    const res = await reservationAxios.patch(`/reservations/${id}/`, {
      stop: new Date().toISOString(),
    });
    return res.data;
  },
  getParkingPhoto: async (id) => {
    const res = await reservationAxios.get(`/reservations/${id}/parking_photo`);
    return res.data;
  },
  getRiderGroupReservations: async (id, params) => {
    const res = await reservationAxios.get("/group-reservations", {
      params: { rider: id, ...params },
    });
    return res.data;
  },
};

const groupReservations = new ApiViewSet(reservationAxios, "group-reservations");

const billing = {
  getWallet: async (riderId) => {
    const res = await billingAxios.get(`/customers/${riderId}/wallet`);
    return { ...res.data, id: riderId };
  },
  getWalletTransactions: async (riderId) => {
    const res = await billingAxios.get(`/customers/${riderId}/transactions`);
    return res.data.results;
  },
  getReceipt: async (reservationId) => {
    const res = await billingAxios.get(`/reservations/${reservationId}/receipt`);
    return { ...res.data, id: reservationId };
  },
  getGroupReceipt: async (groupReservationId) => {
    const res = await billingAxios.get(`/reservations/${groupReservationId}/receipt`, {
      params: { group_reservation: true },
    });
    return { ...res.data, id: groupReservationId };
  },
  getCredits: async (reservationId) => {
    const res = await billingAxios.get(`/reservations/${reservationId}/credits`);
    return { ...res.data, id: reservationId };
  },
  creditWallet: async (reservationId, amount) => {
    const res = await billingAxios.post(`/reservations/${reservationId}/credit_wallet`, {
      amount,
    });
    return { ...res.data, id: reservationId };
  },
  refundSinglePassUse: async (reservationId) => {
    const res = await billingAxios.post(`/reservations/${reservationId}/refund_single_pass_use`);
    return { ...res.data, id: reservationId };
  },
  getBillingTrip: async (reservationId) => {
    const res = await billingAxios.get("/trips", { params: { reservation_id: reservationId } });
    return res.data?.results?.[0];
  },
  cashOutTransaction: async (customerId, transactionId, amount) => {
    const res = await billingAxios.post(
      `/customers/${customerId}/wallet/deposits/${transactionId}/cash_out`,
      {
        amount,
      },
    );
    return res.data;
  },
  cashOutTripTransaction: async (tripId, tripTransactionId, amount) => {
    const res = await billingAxios.post(`/trips/${tripId}/payments/${tripTransactionId}/cash_out`, {
      amount,
    });
    return res.data;
  },
  cashOutFineTransaction: async (tripId, fineTransactionId, amount) => {
    const res = await billingAxios.post(`/trips/${tripId}/fines/${fineTransactionId}/cash_out`, {
      amount,
    });
    return res.data;
  },
  cashOutPassTransaction: async (customerId, passTransactionId, amount) => {
    const res = await billingAxios.post(
      `/customers/${customerId}/pass-transactions/${passTransactionId}/cash_out`,
      {
        amount,
      },
    );
    return res.data;
  },
  addBonus: async (id, amount) => {
    const res = await billingAxios.post(`/customers/${id}/wallet/bonus`, {
      bonus_amount: amount,
    });
    return { ...res.data, id };
  },
  removeBonus: async (id, amount) => {
    const res = await billingAxios.delete(`/customers/${id}/wallet/bonus`, {
      data: { bonus_amount: amount },
    });
    return { ...res.data, id };
  },
  zeroOutNegativeBalance: async (id) => {
    const res = await billingAxios.post(`/customers/${id}/wallet/zero_out`);
    return { ...res.data, id };
  },
  getRatePlan: async (id) => {
    const res = await billingAxios.get(`/rate-plans/${id}`);
    return { ...res.data, id };
  },
  createRatePlan: async (data) => {
    const res = await billingAxios.post("/rate-plans", data);
    return res.data;
  },
  updateRatePlan: async (id, data) => {
    const res = await billingAxios.patch(`/rate-plans/${id}`, data);
    return res.data;
  },
  getCoupon: async (id, type) => {
    const res = await billingAxios.get(
      `/coupons/${type === "unlock" ? "free_unlock" : type}/${id}`,
    );
    return res.data;
  },
  getCouponGroup: async (groupId) => {
    const res = await billingAxios.get(`/coupon-groups/${groupId}`);
    return res.data;
  },
  createCouponGroup: async (data) => {
    const res = await billingAxios.post("/coupon-groups", data);
    return res.data;
  },

  updateCouponGroup: async (groupId, data) => {
    const res = await billingAxios.patch(`/coupon-groups/${groupId}/update-coupons`, data);
    return res.data;
  },

  listCouponGroups: async (params) => {
    const res = await billingAxios.get("/coupon-groups", { params });
    return res.data;
  },
  exportCouponGroup: async (id) => {
    const res = await billingAxios.get(`/coupon-groups/${id}/export`);
    return res;
  },
  getCouponsByGroupId: async (type, params) => {
    const res = await billingAxios.get(`/coupons/${type === "unlock" ? "free_unlock" : type}`, {
      params,
    });
    return res.data;
  },
  listCoupons: async (type, params) => {
    const res = await billingAxios.get(`/coupons/${type === "unlock" ? "free_unlock" : type}`, {
      params,
    });
    return res.data;
  },
  updateCoupons: async (id, type, data) => {
    const res = await billingAxios.patch(
      `/coupons/${type === "unlock" ? "free_unlock" : type}/${id}`,
      data,
    );
    return res.data;
  },
  createCoupons: async (type, data) => {
    const res = await billingAxios.post(
      `/coupons/${type === "unlock" ? "free_unlock" : type}`,
      data,
    );
    return res.data;
  },
  getCustomerCoupons: async (id) => {
    const res = await billingAxios.get(`/customers/${id}/coupons`);
    return res.data;
  },
  getPassOffers: async (params) => {
    const res = await billingAxios.get("/passes/offers", { params });
    return res.data;
  },
  createPassOffer: async (data) => {
    const res = await billingAxios.post("/passes/offers", data);
    return res.data;
  },
  updatePassOffer: async (id, data) => {
    const res = await billingAxios.patch(`/passes/offers/${id}`, data);
    return res.data;
  },
  getPassOfferTypes: async () => {
    const res = await billingAxios.get("/passes/offertypes");
    return res.data;
  },
  getActivePass: async (customerId) => {
    const res = await billingAxios.get("/passes/passes", {
      params: { customer: customerId, disabled: false },
    });
    return res.data.results;
  },
  deactivatePass: async (passId) => {
    const res = await billingAxios.patch(`/passes/passes/${passId}`, { disabled: true });
    return res.data;
  },
  addPass: async (data) => {
    const res = await billingAxios.post("/passes/passes", data);
    return res.data;
  },
  createParkingIncentive: async (data) => {
    const res = await billingAxios.post("/parking-incentives", data);
    return res.data;
  },

  // This will eventually be done via query params.
  getPassTransactions: async (customerId, passId) => {
    const res = await billingAxios.get(`/customers/${customerId}/pass-transactions`);
    const results = res.data.results;
    const passTransactions = results.filter((el) => el.pass_id === passId);
    return passTransactions;
  },
  issueFine: async (tripId, data) => {
    const res = await billingAxios.post(`/trips/${tripId}/fines`, data);
    return res.data;
  },
  listFineTypes: async () => {
    const res = await billingAxios.get("/fine-types");
    return res.data.results;
  },
};

const messaging = {
  list: async (id) => {
    const res = await reservationAxios.get(`/toast-messages?fleets=${id}`);
    return res.data.results;
  },
  get: async (id) => {
    const res = await reservationAxios.get(`/toast-messages/${id}`);
    return res.data;
  },
  create: async (data) => {
    const res = await reservationAxios.post("/toast-messages", data);
    return res.data;
  },
  edit: async (id, data) => {
    const res = await reservationAxios.put(`/toast-messages/${id}`, data);
    return res.data;
  },
};

const registration = {
  manifest: async (manifestFile, customer, fleetName, emails, cluster) => {
    const formData = new FormData();
    formData.append("customer", customer);
    formData.append("fleet", fleetName);
    formData.append("emails", emails);
    formData.append("file", manifestFile);
    formData.append("cluster", cluster);

    const res = await registrationAxios.post("/registration/manifest", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return res.data;
  },
  shipping: async (emails) => {
    const formData = new FormData();
    formData.append("emails", emails);

    const res = await registrationAxios.post("/registration/shipping", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return res.data;
  },
  register: async (vehicleFINs, customer, fleetName, emails, cluster) => {
    const res = await registrationAxios.post("/registration/register", {
      vehicles: vehicleFINs,
      customer,
      fleet: fleetName,
      emails,
      cluster,
    });
    return res.data;
  },
  swapSerializedComponent: async (
    vehicleSerial,
    componentType,
    componentSerial,
    provisioned = null, // AKA new/used; only required for sc
    simId = null, // only required for sc
    cluster,
  ) => {
    const res = await registrationAxios.post("/registration/swap-serialized-component", {
      vehicle_serial_number: vehicleSerial,
      component_type: componentType,
      component_serial_number: componentSerial,
      provisioned,
      sim_id: simId,
      cluster,
    });
    return res.data;
  },
  swapSim: async (vehicleSerialNumber, simId, cluster) => {
    const res = await registrationAxios.post("/registration/swap-sim", {
      vehicle_serial_number: vehicleSerialNumber,
      sim_id: simId,
      cluster,
    });
    return res.data;
  },
  removeSerializedComponent: async (
    vehicleSerial,
    componentType,
    componentSerial,
    cluster,
  ) => {
    const res = await registrationAxios.delete(`/registration/swap-serialized-component?vehicle_serial_number=${vehicleSerial}&component_type=${componentType}&component_serial_number=${componentSerial}&cluster=${cluster}`);
    return res.data;
  },
  removeSim: async (vehicleSerialNumber, simId, cluster) => {
    const res = await registrationAxios.delete(`/registration/swap-sim?vehicle_serial_number=${vehicleSerialNumber}&sim_id=${simId}&cluster=${cluster}`);
    return res.data;
  },
};

const geocoding = {
  forward: async (locationAsString, params = {}) => {
    const location = encodeURIComponent(locationAsString);
    // passing location to url, as Axios params URI encoding is different
    const res = await geocodingAxios.get(`?q=${location}`, { params });
    return res.data?.results;
  },
};

const commandStatus = {
  getForVehicle: async (vehicleId, force = false) => {
    const cacheKey = `VEHICLE_COMMAND_HISTORY_${vehicleId}`;
    const cachedData = sessionStorage.getItem(cacheKey);
    if (cachedData && force === false) {
      return JSON.parse(cachedData);
    }
    const res = await vehicleAxios.get(`/command-statuses/?vehicle=${vehicleId}`);
    sessionStorage.setItem(cacheKey, JSON.stringify(res.data));
    return res.data;
  },
};

const geofenceApi = {
  getGeofenceBinaries: async (fleet) => {
    const formData = new FormData();
    formData.set("fleet", fleet);
    const res = await geofenceAxios.post("/binaries", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      responseType: "arraybuffer",
    });
    return res.data;
  },
  uploadGeofenceBinaries: async (version, fleet) => {
    const formData = new FormData();
    formData.set("version", version);
    formData.set("fleet", fleet);
    const res = await geofenceAxios.post("/packages/generate-and-create", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return res.data;
  },
  getPackageCreationStatus: async (packageId) => {
    const res = await geofenceAxios.get(`/packages/${packageId}/status`);
    return res.data;
  },
  deleteGeofencePackage: async (packageId) => {
    const res = await geofenceAxios.delete(`/packages/${packageId}/`);
    return res.data;
  },
};

export default {
  fleets,
  markets,
  vehicle,
  trips,
  riders,
  reservations,
  groupReservations,
  billing,
  messaging,
  auth,
  geofences,
  firmware,
  firmwarePackages,
  firmwarePackageApplications,
  geofencePackages,
  geofencePackageApplications,
  registration,
  geocoding,
  commandStatus,
  geofenceApi,
};
